import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import ActingBodiesCommandsDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import VacancyLevelDropdown from '../../../components/deac/VacancyLevelDropdown';
import VacancyTagDropdown from '../../../components/deac/VacancyTagDropdown';

interface Props {
    filters: DeacParamList['VacanciesTemplates'];
    setFilters: React.Dispatch<React.SetStateAction<DeacParamList['VacanciesTemplates']>>;
    textFilter: string;
    setTextFilter: (value: string) => void;
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<DeacParamList, 'VacanciesTemplates'>>>();
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyCommandId, setActingBodyCommandId] = useState<number>(filters.actingBodyId ?? 0);
    const [levelsIds, setLevelsIds] = useState<number[]>(filters.levelsIds ?? []);
    const [tagsIds, setTagsIds] = useState<number[]>(filters.tagsIds ?? []);

    const handleCleanFilter = () => {
        setTextFilter('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setActingBodyCommandId(0);
        setLevelsIds([]);
        setTagsIds([]);
    };

    const handleFilter = () => {

        navigation.setParams({
            ...filters,
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
            levelsIds: levelsIds.length > 0 ? levelsIds : undefined,
            tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
        });

        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            actingBodyCommandId: actingBodyCommandId !== 0 ? actingBodyCommandId : undefined,
            levelsIds: levelsIds.length > 0 ? levelsIds : undefined,
            tagsIds: tagsIds.length > 0 ? tagsIds : undefined,
        });
    };

    const getActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies_templates) {
            return actingBodyId;
        }

        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter}>
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />

            {authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies_templates &&
                <ActingBodiesDropdown
                    includeAllOption={true}
                    value={actingBodyId}
                    setValue={setActingBodyId}
                    zIndex={9}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }

            {(authenticatedUser.permissions.deac_manage_all_acting_bodies_vacancies_templates ||
                authenticatedUser.permissions.deac_manage_acting_body_units_vacancies_templates)
            && <ActingBodiesUnitsDropdown
                includeAllOption={true}
                value={actingBodyUnitId}
                setValue={setActingBodyUnitId}
                actingBodyId={getActingBodyId()}
                zIndex={8}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />
            }

            <ActingBodiesCommandsDropdown
                includeAllOption={true}
                value={actingBodyCommandId}
                setValue={setActingBodyCommandId}
                actingBodyId={getActingBodyId()}
                disabled={false}
                zIndex={7}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <VacancyLevelDropdown
                actingBodyId={getActingBodyId()}
                setValue={setLevelsIds}
                value={levelsIds}
                zIndex={5}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            <VacancyTagDropdown
                setValue={setTagsIds}
                value={tagsIds}
                zIndex={3}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));

export default Filter;
